import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineLoading,
  RadioButton,
  RadioButtonGroup,
  Stack,
  TextInput,
  Tile,
} from "@carbon/react";
import type { ImportFormatResponse, ImportProbeResponse } from "../../types";
import SavedFormatNotice from "./SavedFormatNotice";

/** Only .xlsx, matching what the backend will accept. */
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** What the backend will take. Checked here too, so an oversized file fails before it is sent. */
const MAX_BYTES = 15 * 1024 * 1024;

/** The shape of a Google Sheets link, as an example rather than as copy — the same in every language. */
const SHEET_URL_EXAMPLE = "https://docs.google.com/spreadsheets/d/...";

type Props = {
  formats: ImportFormatResponse[];
  isStarting: boolean;
  isProbing: boolean;
  probe: ImportProbeResponse | null;
  onFileChosen: (file: File | null) => void;
  /** The description is always empty now: any file is read from its own columns. */
  onStart: (file: File, description: string) => void;
  onStartSheet: (
    spreadsheetUrl: string,
    sheetName: string,
    description: string
  ) => void;
};

/** Where the rows are. Everything after this step is the same either way. */
type Source = "file" | "sheet";

/**
 * Choosing a file, and nothing else: any spreadsheet is read from its own columns.
 *
 * The file is looked at the moment it is chosen, so one the company has imported before is
 * recognised and read the way it was last time.
 */
const UploadStep = ({
  formats,
  isStarting,
  isProbing,
  probe,
  onFileChosen,
  onStart,
  onStartSheet,
}: Props) => {
  const { t } = useTranslation();
  const [source, setSource] = useState<Source>("file");
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recognised = probe?.match ?? null;

  const refuse = (message: string) => {
    setError(message);
    setFile(null);
    onFileChosen(null);
  };

  const chooseFile = (chosen: File | null) => {
    setError(null);
    if (!chosen) return;
    const looksRight =
      chosen.type === XLSX_MIME || chosen.name.toLowerCase().endsWith(".xlsx");
    if (!looksRight) {
      refuse(t("purchaseRequests.import.onlyXlsx"));
      return;
    }
    if (chosen.size > MAX_BYTES) {
      refuse(t("purchaseRequests.import.tooBig"));
      return;
    }
    setFile(chosen);
    onFileChosen(chosen);
  };

  /**
   * Forgets the chosen file. Carbon's drop container clears its own input on every click, so
   * choosing the same file again still fires a change.
   */
  const clearFile = () => {
    setFile(null);
    setError(null);
    onFileChosen(null);
  };

  const ready = source === "file" ? !!file : spreadsheetUrl.trim().length > 0;

  const start = () => {
    if (source === "file" && file) {
      onStart(file, "");
    } else if (source === "sheet") {
      onStartSheet(spreadsheetUrl.trim(), sheetName.trim(), "");
    }
  };

  return (
    <div className="pr-import__upload">
      <Tile className="pr-import__panel">
        <h3 className="pr-import__title">
          {t("purchaseRequests.import.chooseFile")}
        </h3>

        <RadioButtonGroup
          className="pr-import__source"
          name="pr-import-source"
          legendText={t("purchaseRequests.import.sourceLegend")}
          valueSelected={source}
          onChange={(value) => setSource(value as Source)}
        >
          <RadioButton
            id="pr-import-source-file"
            value="file"
            labelText={t("purchaseRequests.import.source.file")}
          />
          <RadioButton
            id="pr-import-source-sheet"
            value="sheet"
            labelText={t("purchaseRequests.import.source.sheet")}
          />
        </RadioButtonGroup>

        {source === "sheet" && (
          <Stack gap={5}>
            <TextInput
              id="pr-import-sheet-url"
              labelText={t("purchaseRequests.import.sheetUrlLabel")}
              placeholder={SHEET_URL_EXAMPLE}
              value={spreadsheetUrl}
              onChange={(event) => setSpreadsheetUrl(event.target.value)}
            />
            <TextInput
              id="pr-import-sheet-name"
              labelText={t("purchaseRequests.import.sheetNameLabel")}
              placeholder={t("purchaseRequests.import.sheetNameHint")}
              helperText={t("purchaseRequests.import.sheetShareHint")}
              value={sheetName}
              onChange={(event) => setSheetName(event.target.value)}
            />
          </Stack>
        )}

        {source === "file" && (
          <Stack gap={3}>
            <FileUploaderDropContainer
              className="pr-import__dropzone"
              accept={[".xlsx"]}
              labelText={t("purchaseRequests.import.dropHere")}
              onAddFiles={(_event, { addedFiles }) =>
                chooseFile(addedFiles[0] ?? null)
              }
            />
            <p className="pr-import__hint">
              {t("purchaseRequests.import.accepted")}
            </p>
            {file && (
              <FileUploaderItem
                name={file.name}
                status="edit"
                iconDescription={t("cta.delete")}
                onDelete={clearFile}
              />
            )}
            {isProbing && (
              <InlineLoading
                description={t("purchaseRequests.import.checkingIfKnown")}
              />
            )}
          </Stack>
        )}

        {error && <p className="pr-import__error-text">{error}</p>}

        <p className="pr-import__hint">
          {t("purchaseRequests.import.privacyNote")}
        </p>

        {formats.length > 0 && !probe && (
          <p className="pr-import__hint">
            {t("purchaseRequests.import.savedFormatsHint")}
          </p>
        )}

        {source === "file" && recognised && (
          <SavedFormatNotice format={recognised} />
        )}

        <div className="pr-import__actions">
          {isStarting ? (
            <InlineLoading description={t("purchaseRequests.import.reading")} />
          ) : (
            <Button onClick={start} disabled={!ready || isProbing}>
              {t("purchaseRequests.import.processFile")}
            </Button>
          )}
        </div>
      </Tile>
    </div>
  );
};

export default UploadStep;
